import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import type { SxProps } from "@mui/material/styles";

import { colord } from "colord";

import { primary } from "app/themes/colors";

type BasePickerProps = {
  sx?: SxProps;
  options: string[];
  value?: string | null;
  onClick: (value: string) => void;
  onDoubleClick?: (value?: string) => void;
};

const baseSx = {
  "&:hover": {
    background: colord(primary[400]).alpha(0.15).toHex()
  },
  "&.Mui-selected": {
    background: primary[400],
    color: "#fff"
  },
  "&.Mui-selected:hover": {
    background: colord(primary[400]).alpha(0.65).toHex(),
    color: "#fff"
  }
};

export default function BasePicker({
  sx = {},
  options,
  value: selectedValue,
  onClick,
  onDoubleClick
}: BasePickerProps) {
  const ref = useRef<any>(null);
  const [lastScrolledValue, setLastScrolledValue] = useState<
    string | null | undefined
  >("");

  useEffect(() => {
    if (lastScrolledValue !== "") {
      setLastScrolledValue(selectedValue);
    } else if (selectedValue !== lastScrolledValue) {
      ref.current = undefined;
    }
  }, [selectedValue, lastScrolledValue]);

  const getRef = (value: string) => {
    if (
      !ref.current &&
      selectedValue === value &&
      value !== lastScrolledValue
    ) {
      setLastScrolledValue(selectedValue);
      return ref;
    }
  };

  return (
    <Box
      height="100%"
      width="100%"
      sx={{
        overflowY: "scroll",
        ...sx
      }}
    >
      {options.map((value) => (
        <MenuItem
          ref={getRef(value)}
          key={value}
          value={value}
          selected={value === selectedValue}
          onClick={() => onClick(value)}
          onDoubleClick={() => {
            if (onDoubleClick) onDoubleClick(value);
          }}
          sx={baseSx}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onDoubleClick?.();
              event.preventDefault();
            }
          }}
        >
          {value}
        </MenuItem>
      ))}
    </Box>
  );
}
